/**
 * Returns the visible text-layer span elements for a given page.
 * Filters out pdfjs .markedContent wrappers and empty spans.
 */
export function getTextLayerElements(pageNumber) {
  const pageNode = document.getElementById(`page-${pageNumber}-text-layer`);
  if (!pageNode) return [];
  const allSpans = pageNode.querySelectorAll('span:not(.markedContent)');
  return Array.from(allSpans).filter((s) => s.textContent !== '');
}

/**
 * Given a parent element, returns an array of all the TEXT_NODEs
 * (in document order) that are descendants of it.
 */
export function getChildTextNodes(rootElement) {
  const textNodes = [];
  const walker = document.createTreeWalker(rootElement, NodeFilter.SHOW_TEXT, null, false);
  let node;
  while ((node = walker.nextNode())) {
    textNodes.push(node);
  }
  return textNodes;
}

/**
 * Given an array of text nodes (in order) and an offset
 * into the *concatenated* text of all the text nodes, find:
 *   – which textNode holds the character at position `globalOffset`,
 *   – and what the local offset within that textNode is.
 *
 * Returns an object { node: TextNode, offset: Number }.
 */
export function whichNodeHasOffset(textNodes, globalOffset) {
  let cumulated = 0;

  for (let i = 0; i < textNodes.length; i++) {
    const txtNode = textNodes[i];
    const len = txtNode.textContent.length;
    if (globalOffset <= cumulated + len) {
      // The desired character is at (globalOffset - cumulated) inside this txtNode
      return {
        node: txtNode,
        offset: globalOffset - cumulated,
      };
    }
    cumulated += len;
  }
  // If we get here, globalOffset was past the end; clamp to last node
  const last = textNodes[textNodes.length - 1];
  return { node: last, offset: last.textContent.length };
}

/**
 * Highlights a match span across one or more text-layer elements.
 * Recursively walks from match.start.element to match.end.element,
 * wrapping each portion in a <keyword> tag.
 *
 * @param {NodeList|Array} pageElements - text-layer spans from getTextLayerElements()
 * @param {number} currentElementNumber - current element index being processed
 * @param {Object} match - { start: { element, offset }, end: { element, offset } }
 * @param {string} className - CSS class for the <keyword> element (e.g. "keyword-abc-0")
 * @param {string} color - background color
 */
export function highlightMatchSpan(pageElements, currentElementNumber, match, className, color) {
  const currentElement = pageElements[currentElementNumber];
  if (!currentElement) {
    console.warn(`[Highlight] Element ${currentElementNumber} not found (max: ${pageElements.length - 1})`);
    return;
  }

  if (currentElement.nodeName === 'BR') {
    if (currentElementNumber + 1 <= match.end.element) {
      highlightMatchSpan(pageElements, currentElementNumber + 1, match, className, color);
    }
    return;
  }

  const startOffset = currentElementNumber === match.start.element ? match.start.offset : 0;
  const endOffset = currentElementNumber === match.end.element ? match.end.offset : currentElement.innerText.length;
  if (
    currentElement.nodeName === 'SPAN' &&
    currentElement.innerText.length >= startOffset &&
    currentElement.innerText.length >= endOffset
  ) {
    const wrapper = document.createElement('keyword');
    wrapper.style.backgroundColor = color;
    wrapper.style.borderRadius = '4px';
    wrapper.classList.add(className);
    wrapRangeInTextNodes(currentElement, startOffset, endOffset, wrapper);

    if (currentElementNumber + 1 <= match.end.element) {
      highlightMatchSpan(pageElements, currentElementNumber + 1, match, className, color);
    }
  }
}

/**
 * Replaces the inner part of `currentElement` from 'startOffset' to 'endOffset'
 * (both measured into the concatenated textContent) with a <span> wrapper.
 */
export function wrapRangeInTextNodes(currentElement, startOffset, endOffset, highlightSpan) {
  // Collect all descendant text nodes in document order
  const textNodes = getChildTextNodes(currentElement);

  // Find start node + local offset
  const { node: startNode, offset: localStart } = whichNodeHasOffset(textNodes, startOffset);
  // Find end node + local offset
  const { node: endNode, offset: localEnd } = whichNodeHasOffset(textNodes, endOffset);

  // Create a Range from (startNode, localStart) to (endNode, localEnd)
  const range = document.createRange();
  range.setStart(startNode, localStart);
  range.setEnd(endNode, localEnd);

  // Extract whatever is inside that Range. This will split text nodes
  //    or existing element children, so you never “partially select” an element (ie ).
  const extracted = range.extractContents();

  // Put the extracted fragment into the new <span>:
  highlightSpan.appendChild(extracted);

  // Insert that <span> at the collapsed Range position
  range.insertNode(highlightSpan);

  // Clean up
  range.detach();
}
